import axios from 'axios'
import { notifications } from '@mantine/notifications'
import { useAuthStore } from '../stores/authStore'

const SAFE_PATTERNS = [
  'email inválido',
  'senha muito curta',
  'campo obrigatório',
  'inválido',
  'já existe',
  'não encontrado',
]

function extractSafeMessage(responseData: unknown): string {
  const data = responseData as Record<string, unknown>
  const msg = data?.detail ?? data?.message
  if (typeof msg === 'string' && SAFE_PATTERNS.some((p) => msg.toLowerCase().includes(p))) {
    return msg
  }
  return 'Dados inválidos, verifique o formulário.'
}

// Em dev, mantém '/api/v1' relativo (vite faz proxy para localhost:8000).
// Em build de produção (Cloudflare Pages, Vercel etc.), VITE_API_BASE_URL
// aponta para o backend absoluto (ex.: https://staging.pmoc123.com.br/api/v1).
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status: number | undefined = error.response?.status
    const original = error.config

    const url: string = original.url ?? ''
    const isAuthRoute = url.includes('/auth/login/') || url.includes('/auth/refresh/')

    if (status === 401 && !original._retry && !isAuthRoute) {
      original._retry = true
      try {
        const refresh = useAuthStore.getState().refreshToken
        const resp = await axios.post(`${API_BASE_URL}/auth/refresh/`, { refresh })
        const newToken = resp.data.access as string
        useAuthStore.getState().updateToken(newToken)
        original.headers.Authorization = `Bearer ${newToken}`
        return api(original)
      } catch {
        useAuthStore.getState().logout()
        notifications.show({
          color: 'red',
          title: 'Sessão expirada',
          message: 'Faça login novamente para continuar.',
        })
        throw error
      }
    }

    if (status !== undefined && status >= 500) {
      notifications.show({
        color: 'red',
        title: 'Erro no servidor',
        message: 'Erro no servidor, tente novamente.',
      })
      throw error
    }

    if (status === 422 || status === 400) {
      notifications.show({
        color: 'orange',
        title: 'Dados inválidos',
        message: extractSafeMessage(error.response?.data),
      })
      throw error
    }

    throw error
  },
)
